/**
 * E03-F05 (TIER-2 fidelity) — structured per-type READ/PREVIEW render for a
 * document. This is the render half of the legacy `app.renderDocContentHtml`:
 * it consumes the typed node tree from {@link buildDocView} and renders it as
 * React. There is NO `dangerouslySetInnerHTML` anywhere — every text node goes
 * through React's own escaping, and inline emphasis is rendered from the safe
 * segments produced by `renderMarkdownInline` (never raw HTML).
 *
 * DocumentEditor mounts this in View mode instead of the flat field dump, so
 * each doc type reads as a real formatted document (JD sections, Interview-Q
 * groups, InMail cards, Kickoff Sheet with the nested Sourcing Sheet + JD copy,
 * the ~24-row Sourcing Sheet, and the emails).
 */
import type { JSX } from 'react';
import { looksLikeHtml } from '@eer/shared';
import { RichTextView } from '../ui/RichTextView.js';
import { buildDocView, type DocViewNode, type InlineText } from './docViewModel.js';
import type { InlineSegment } from './docGenerationModel.js';

/**
 * Render one run of inline text.
 *
 * Two paths, chosen by the value itself (KS-07):
 *   · HTML → {@link RichTextView}, which sanitizes with the shared allow-list and
 *     applies the prose styling. This is what makes a generated document read as a
 *     formatted document now that the prompts emit HTML;
 *   · anything else → the legacy Markdown segments, React-escaped as before.
 *
 * The Markdown path is not dead code: documents generated before the migration are
 * still Markdown in the DB (the client's existing rows are test data and were left
 * alone by decision), and a person can still type `**bold**` into a field.
 */
function Inline({ text }: { text: InlineText }): JSX.Element {
  if (looksLikeHtml(text.raw)) return <RichTextView value={text.raw} className="text-sm" />;
  return (
    <>
      {text.segments.map((s: InlineSegment, i: number) => {
        if (s.kind === 'bold') return <strong key={i}>{s.text}</strong>;
        if (s.kind === 'italic') return <em key={i}>{s.text}</em>;
        if (s.kind === 'underline') return <u key={i}>{s.text}</u>;
        // PK-QA-23 — a real anchor built from the parsed segment (href + text are
        // React-set, never innerHTML). Opens in a new tab with a safe rel.
        if (s.kind === 'link')
          return (
            <a
              key={i}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--color-primary)] underline hover:text-[var(--color-primary-hover)] break-words"
            >
              {s.text}
            </a>
          );
        return <span key={i}>{s.text}</span>;
      })}
    </>
  );
}

/**
 * KS3-04 — is this whole run a single HTML value?
 *
 * `displayLines` collapses an HTML value to ONE `InlineText` (there is nothing to
 * split: the tags carry the structure). That distinction decides how the run is
 * wrapped, and it matters: the plain-text branch needs `whitespace-pre-wrap`
 * because a recruiter's line breaks are the meaning, while HTML must NOT get it —
 * the model writes `</p>\n\n<p>` between blocks, and under `pre-wrap` each of
 * those insignificant newlines becomes a visible blank line. On the live follow-up
 * of project 201 that inflated the body from 3478px to 8004px: 57% of the height
 * was air, which is exactly the "огромные промежутки между строками" report.
 *
 * The second defect it removes is structural: a `<p>` wrapper around RichTextView's
 * `<div>` is invalid nesting, so the browser silently closed the paragraph early
 * and left an extra empty one behind.
 */
function isHtmlRun(lines: readonly InlineText[]): boolean {
  return lines.length === 1 && looksLikeHtml(lines[0]!.raw);
}

/** Render a single view node. */
function Node({ node }: { node: DocViewNode }): JSX.Element | null {
  switch (node.kind) {
    case 'title':
      return (
        <div data-testid="doc-view-title">
          <h2 className="text-xl font-bold text-[var(--color-text-strong)]">{node.text}</h2>
          {node.subtitle && (
            <p className="text-xs text-[var(--color-text-muted)] mt-1">{node.subtitle}</p>
          )}
        </div>
      );

    case 'heading': {
      const cls =
        node.level === 2
          ? 'text-lg font-bold text-[var(--color-text-strong)] mt-2'
          : node.level === 3
            ? 'text-base font-bold text-[var(--color-text-strong)] mt-3 border-b border-[var(--color-border)] pb-1'
            : 'font-bold text-[var(--color-text-strong)] mb-1';
      return (
        <div
          data-testid="doc-view-section"
          data-section={node.text}
          className={cls}
          role="heading"
          aria-level={node.level}
        >
          {node.text}
        </div>
      );
    }

    case 'prose':
      if (isHtmlRun(node.lines)) {
        return (
          <RichTextView
            value={node.lines[0]!.raw}
            testId="doc-view-prose"
            className="text-sm text-[var(--color-text)]"
          />
        );
      }
      return (
        <div
          data-testid="doc-view-prose"
          className="text-sm text-[var(--color-text)] whitespace-pre-wrap leading-relaxed"
        >
          {node.lines.map((l, i) => (
            <p key={i} className="mb-1">
              <Inline text={l} />
            </p>
          ))}
        </div>
      );

    case 'list':
      return node.ordered ? (
        <ol
          data-testid="doc-view-list"
          className="list-decimal ml-4 space-y-1 text-sm text-[var(--color-text)]"
        >
          {node.items.map((it, i) => (
            <li key={i}>
              <Inline text={it} />
            </li>
          ))}
        </ol>
      ) : (
        <ul
          data-testid="doc-view-list"
          className="list-disc ml-4 space-y-1 text-sm text-[var(--color-text)]"
        >
          {node.items.map((it, i) => (
            <li key={i}>
              <Inline text={it} />
            </li>
          ))}
        </ul>
      );

    case 'comment':
      return (
        <div
          data-testid="doc-view-comment"
          className="text-xs italic text-[var(--color-text-muted)] mt-1 pl-2 border-l-2 border-[var(--color-border)]"
        >
          {node.text}
        </div>
      );

    case 'row':
      return (
        <div
          data-testid="doc-view-row"
          data-field={node.label}
          data-empty={node.empty ? 'true' : undefined}
          className="flex gap-2 py-1.5 border-b border-[var(--color-border)] text-sm"
        >
          <span className="text-[var(--color-text-muted)] w-1/3 text-xs font-medium">
            {node.label}:
          </span>
          {/* A div, not a span: RichTextView renders a block element, and a block
              inside a span is invalid markup. */}
          <div className="flex-1 text-[var(--color-text)]">
            {node.empty ? <span className="block h-4" aria-hidden /> : <Inline text={node.value} />}
          </div>
        </div>
      );

    case 'block':
      return (
        <div
          data-testid="doc-view-block"
          data-field={node.label}
          data-empty={node.empty ? 'true' : undefined}
          className="py-1.5 border-b border-[var(--color-border)]"
        >
          <span className="text-[var(--color-text-muted)] text-xs font-medium">{node.label}:</span>
          {node.empty ? (
            // KS3-07 — the section stays visible with room under it, so a blank
            // field reads as "nothing found yet", not as a section that does not
            // exist in this document.
            <div className="h-8" aria-hidden />
          ) : isHtmlRun(node.lines) ? (
            <RichTextView value={node.lines[0]!.raw} className="mt-1 text-sm" />
          ) : (
            <div className="text-sm text-[var(--color-text)] mt-1 whitespace-pre-wrap">
              {node.lines.map((l, i) => (
                <p key={i} className="mb-0.5">
                  <Inline text={l} />
                </p>
              ))}
            </div>
          )}
        </div>
      );

    case 'divider':
      return <hr data-testid="doc-view-divider" className="my-3 border-[var(--color-border)]" />;

    case 'card':
      return (
        <div
          data-testid="doc-view-card"
          className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-sm"
        >
          <div className="font-bold text-[var(--color-text-strong)] mb-2">{node.title}</div>
          {node.subject && (
            <p className="text-xs text-[var(--color-text-muted)] mb-1">
              <strong>Subject:</strong> {node.subject}
            </p>
          )}
          {/* E03-F05-T10 — ONE testid across BOTH branches (the `RichTextOrPlain`
              convention). Since KS-07 the generator emits HTML InMail bodies, so the
              card takes the RichTextView branch and the plain branch's
              `whitespace-pre-wrap` class no longer exists in the DOM; anything that
              located the body by that class silently stopped finding the text even
              though it renders correctly. The body must be addressable by WHAT it is,
              not by which formatting branch produced it. */}
          {isHtmlRun(node.body) ? (
            <RichTextView
              value={node.body[0]!.raw}
              testId="doc-view-card-body"
              className="text-[var(--color-text)]"
            />
          ) : (
            <div
              data-testid="doc-view-card-body"
              className="whitespace-pre-wrap text-[var(--color-text)]"
            >
              {node.body.map((l, i) => (
                <p key={i} className="mb-0.5">
                  <Inline text={l} />
                </p>
              ))}
            </div>
          )}
        </div>
      );

    case 'callout': {
      const cls =
        node.tone === 'warning'
          ? 'bg-[var(--color-warning)]/10 border-l-4 border-[var(--color-warning)]'
          : 'bg-[var(--color-surface-muted)] border-l-4 border-[var(--color-border)]';
      return (
        <div
          data-testid="doc-view-callout"
          data-tone={node.tone}
          className={`rounded p-3 text-xs ${cls}`}
        >
          <strong>{node.title}:</strong>
          {node.text && (
            <span>
              {' '}
              <Inline text={node.text} />
            </span>
          )}
          {node.items && node.items.length > 0 && (
            <ul className="list-disc ml-4 mt-1">
              {node.items.map((it, i) => (
                <li key={i}>
                  <Inline text={it} />
                </li>
              ))}
            </ul>
          )}
        </div>
      );
    }

    default:
      return null;
  }
}

export interface DocumentViewProps {
  readonly docType: string;
  readonly content: unknown;
}

/**
 * Render a document's content as a structured, per-type formatted document
 * (View mode). Pure presentation: the mapping lives in {@link buildDocView}.
 */
export function DocumentView({ docType, content }: DocumentViewProps): JSX.Element {
  const nodes = buildDocView(docType, content);
  return (
    <div data-testid="document-view" className="flex flex-col gap-3">
      {nodes.map((node, i) => (
        <Node key={i} node={node} />
      ))}
    </div>
  );
}
